import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CANCEL_REASONS } from "@/lib/constants";
import { toast } from "sonner";
import { Plane, MapPin, ArrowRight, Users, Loader2, Car, Package, KeyRound, Inbox, User } from "lucide-react";

const TABS = ["Upcoming", "Active", "Completed", "Cancelled"];

export default function MyTrips() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTrip, setCancelTrip] = useState(null);
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, a, cn] = await Promise.all([api.get("/trips/mine"), api.get("/deliveries/assigned"), api.get("/connections/mine")]);
      setTrips(t.data.trips);
      setAssigned(a.data.deliveries);
      setRequests(cn.data.connections.filter((c) => c.status === "pending" && c.my_role === "traveller"));
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const respondRequest = async (cid, action) => {
    setBusy(true);
    try {
      await api.post(`/connections/${cid}/${action}`);
      toast.success(action === "accept" ? "Request accepted. The package is now in 'Packages to deliver'." : "Request declined.");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/trips/${id}`, { status }); toast.success(`Trip marked ${status}.`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const r = reason === "Other" ? otherReason : reason;
      const { data } = await api.post(`/trips/${cancelTrip.id}/cancel`, { reason: r });
      toast.success(data.affected > 0 ? `Trip cancelled. ${data.affected} connected sender(s) notified.` : "Trip cancelled.");
      setCancelTrip(null); setReason(""); setOtherReason(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="My Trips" subtitle="Manage the trips you've published and the packages you're carrying." action={<Button onClick={() => (window.location.href = "/create-trip")} className="rounded-full" data-testid="mytrips-create">New Trip</Button>} />

      {!loading && requests.length > 0 && (
        <div className="mb-6" data-testid="delivery-requests-section">
          <h2 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2"><Inbox className="w-4 h-4 text-primary" /> Delivery requests</h2>
          <div className="space-y-3">
            {requests.map((c) => (
              <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`delivery-request-${c.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      <User className="w-4 h-4 text-primary flex-shrink-0" /><span className="truncate">{c.other_user?.name || "A sender"} asked you to carry a package</span>
                    </p>
                    {c.delivery && (
                      <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate">{c.delivery.pickup}</span>
                        <ArrowRight className="w-3 h-3 flex-shrink-0" /><span className="truncate">{c.delivery.destination}</span>
                      </div>
                    )}
                    {c.message && <p className="mt-1 text-xs text-slate-400">"{c.message}"</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button size="sm" onClick={() => respondRequest(c.id, "accept")} disabled={busy} className="rounded-lg" data-testid={`request-accept-${c.id}`}>Accept</Button>
                    <Button size="sm" variant="outline" onClick={() => respondRequest(c.id, "decline")} disabled={busy} className="rounded-lg" data-testid={`request-decline-${c.id}`}>Decline</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && assigned.length > 0 && (
        <div className="mb-6" data-testid="assigned-deliveries-section">
          <h2 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-primary" /> Packages to deliver</h2>
          <div className="space-y-3">
            {assigned.map((dl) => {
              const needsPickup = ["Accepted", "Matched", "Pickup Pending"].includes(dl.status);
              const needsDelivery = ["Picked Up", "In Transit", "Arrived", "Out for Delivery"].includes(dl.status);
              return (
                <div key={dl.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex items-center justify-between gap-3" data-testid={`assigned-delivery-${dl.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      <MapPin className="w-4 h-4 text-primary flex-shrink-0" /><span className="truncate">{dl.pickup}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><span className="truncate">{dl.destination}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
                      <span className="font-mono">{dl.id}</span><StatusBadge status={dl.status} />
                    </div>
                  </div>
                  <Button size="sm" onClick={() => navigate(`/deliveries/${dl.id}`)} className="rounded-lg flex-shrink-0" data-testid={`assigned-open-${dl.id}`}>
                    {(needsPickup || needsDelivery) && <KeyRound className="w-3.5 h-3.5" />}
                    {needsPickup ? "Enter Pickup PIN" : needsDelivery ? "Enter Delivery PIN" : "Open"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-primary animate-spin" /></div> : (
        <Tabs defaultValue="Upcoming">
          <TabsList className="rounded-xl mb-5 flex-wrap h-auto">{TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`mytrips-tab-${t.toLowerCase()}`} className="rounded-lg">{t}</TabsTrigger>)}</TabsList>
          {TABS.map((tab) => {
            const list = trips.filter((t) => t.status === tab);
            return (
              <TabsContent key={tab} value={tab}>
                {list.length === 0 ? (
                  <EmptyState icon={Plane} title={`No ${tab.toLowerCase()} trips.`} description={tab === "Upcoming" ? "Publish a trip to start carrying packages." : "Nothing here yet."} actionLabel={tab === "Upcoming" ? "Create your first trip" : undefined} actionTo="/create-trip" testid={`empty-trips-${tab.toLowerCase()}`} />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {list.map((t) => (
                      <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`trip-card-${t.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-slate-400">{t.id}</span>
                          <StatusBadge status={t.status} />
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-800">
                          <MapPin className="w-4 h-4 text-primary flex-shrink-0" /><span className="truncate">{t.origin}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-400" /><span className="truncate">{t.destination}</span>
                        </div>
                        <div className="mt-2 text-xs text-slate-500">{t.travel_date} {t.travel_time} · {t.capacity}</div>
                        {t.travel_mode && <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500"><Car className="w-3.5 h-3.5" /> {t.travel_mode}{t.vehicle_model ? ` · ${t.vehicle_model}` : ""}</div>}
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><Users className="w-3.5 h-3.5" /> {t.connections || 0} connection(s)</div>
                        <div className="mt-4 flex gap-2 flex-wrap">
                          {t.status === "Upcoming" && <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setStatus(t.id, "Active")} data-testid={`trip-activate-${t.id}`}>Mark Active</Button>}
                          {["Upcoming", "Active"].includes(t.status) && <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setStatus(t.id, "Completed")} data-testid={`trip-complete-${t.id}`}>Complete</Button>}
                          {["Upcoming", "Active"].includes(t.status) && <Button size="sm" variant="ghost" className="rounded-lg text-rose-600" onClick={() => { setCancelTrip(t); setReason(""); setOtherReason(""); }} data-testid={`trip-cancel-${t.id}`}>Cancel Trip</Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      <AlertDialog open={!!cancelTrip} onOpenChange={(o) => !o && setCancelTrip(null)}>
        <AlertDialogContent data-testid="trip-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this trip?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTrip?.connections > 0
                ? "This trip has accepted delivery connections. Cancelling will notify those senders. This cannot be undone."
                : "This trip will be moved to Cancelled and will no longer be discoverable."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Reason (optional)</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="trip-cancel-reason" className="rounded-xl"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>{CANCEL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            {reason === "Other" && <Textarea value={otherReason} onChange={(e) => setOtherReason(e.target.value)} placeholder="Tell us more" className="rounded-xl" rows={2} data-testid="trip-cancel-other" />}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trip-cancel-keep">Keep Trip</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="trip-cancel-confirm">{busy ? "Cancelling…" : "Cancel Trip"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
